import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PARTY_KEY_COMPOSITION_RESOLVE,
  API_PARTY_KEY_COMPOSITION_LIST,
  API_PARTY_KEY_COMPOSITION_CREATE,
  API_PARTY_KEY_COMPOSITION_SUPERSEDE,
  API_PARTY_KEY_COMPOSITION_DELETE,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror SmartMatrix.Domain.Core.Parties.Messages.KeyCompositionProfile_*.
// ============================================================================

export interface KeyCompositionProfile {
  id: number;
  scopePath: string;
  scopeLevel: number;

  keyType: string;
  mixKind: string;

  componentsJson: string;
  delimiter: string;
  casePolicy: string;
  nullPolicy: string;
  nullPlaceholder?: string | null;
  version: number;

  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  internalRemark?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

export type KeyCompositionRow = KeyCompositionProfile;

export interface KeyCompositionResolveParams {
  tenantId: number;
  keyType: string;
  mixKind: string;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  channelCode?: string | null;
  poNumber?: string | null;
  asOfUtc?: string | null;
}

export interface KeyCompositionListFilters {
  tenantId: number;
  scopePathPrefix?: string;
  scopeLevel?: number;
  keyType?: string;
  mixKind?: string;
  includeExpired?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface KeyCompositionListResponse {
  items: KeyCompositionRow[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface KeyCompositionCreateRequest {
  scopePath: string;
  keyType: string;
  mixKind: string;

  componentsJson: string;
  delimiter: string;
  casePolicy: string;
  nullPolicy: string;
  nullPlaceholder?: string | null;
  version?: number;

  effectiveFrom: string;
  effectiveTo?: string | null;
  internalRemark?: string | null;
  createdBy?: string | null;
}

export interface KeyCompositionSupersedeRequest {
  newEffectiveFrom: string;
  newEffectiveTo?: string | null;

  newComponentsJson?: string | null;
  newDelimiter?: string | null;
  newCasePolicy?: string | null;
  newNullPolicy?: string | null;
  newNullPlaceholder?: string | null;
  newVersion?: number | null;

  newInternalRemark?: string | null;
  modifiedBy?: string | null;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

interface KeyCompositionResolveEnvelope {
  profile: KeyCompositionProfile | null;
}

const NOT_FOUND_STATUS_CODE = -2510;

// ============================================================================
// Service
// ============================================================================

export const keyCompositionService = {
  /**
   * Resolve the strictest-winning profile. Returns `null` on
   * `Not_Found=-2510` (treated as a cache miss, not an error).
   */
  async resolve(params: KeyCompositionResolveParams): Promise<KeyCompositionProfile | null> {
    try {
      const response = await apiClient.get<ResultEnvelope<KeyCompositionResolveEnvelope>>(
        API_PARTY_KEY_COMPOSITION_RESOLVE,
        {
          params: {
            tenantId: params.tenantId,
            keyType: params.keyType,
            mixKind: params.mixKind,
            buyerPartyId: params.buyerPartyId ?? undefined,
            supplierPartyId: params.supplierPartyId ?? undefined,
            channelCode: params.channelCode ?? undefined,
            poNumber: params.poNumber ?? undefined,
            asOfUtc: params.asOfUtc ?? undefined,
          },
        }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === NOT_FOUND_STATUS_CODE) return null;
        return null;
      }
      return response.data.data?.profile ?? null;
    } catch (error) {
      logger.error('Failed to resolve key-composition profile', error);
      throw error;
    }
  },

  async list(filters: KeyCompositionListFilters): Promise<KeyCompositionListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<KeyCompositionListResponse>>(
        API_PARTY_KEY_COMPOSITION_LIST,
        {
          params: {
            tenantId: filters.tenantId,
            scopePathPrefix: filters.scopePathPrefix || undefined,
            scopeLevel: filters.scopeLevel,
            keyType: filters.keyType || undefined,
            mixKind: filters.mixKind || undefined,
            includeExpired: filters.includeExpired,
            pageIndex: filters.pageIndex ?? 1,
            pageSize: filters.pageSize ?? 50,
          },
        }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list key-composition profiles');
      }
      return response.data.data ?? { items: [], pageIndex: 1, pageSize: 50, totalCount: 0 };
    } catch (error) {
      logger.error('Failed to list key-composition profiles', error);
      throw error;
    }
  },

  async create(tenantId: number, request: KeyCompositionCreateRequest): Promise<{ id: number }> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ id: number }>>(
        API_PARTY_KEY_COMPOSITION_CREATE,
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to create key-composition profile');
      }
      toast.success('Key-composition profile created');
      return response.data.data ?? { id: 0 };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create key-composition profile: ${msg}`);
      throw error;
    }
  },

  async supersede(
    tenantId: number,
    id: number,
    request: KeyCompositionSupersedeRequest
  ): Promise<{ newId: number; expiredId: number }> {
    try {
      const response = await apiClient.put<ResultEnvelope<{ newId: number; expiredId: number }>>(
        API_PARTY_KEY_COMPOSITION_SUPERSEDE(id),
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to supersede key-composition profile');
      }
      toast.success('Key-composition profile superseded');
      return response.data.data ?? { newId: 0, expiredId: id };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to supersede key-composition profile: ${msg}`);
      throw error;
    }
  },

  async remove(tenantId: number, id: number): Promise<void> {
    try {
      const response = await apiClient.delete<ResultEnvelope<unknown>>(
        API_PARTY_KEY_COMPOSITION_DELETE(id),
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to delete key-composition profile');
      }
      toast.success('Key-composition profile deleted');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete key-composition profile: ${msg}`);
      throw error;
    }
  },
};
